/* v3.2.8 · Session-scoped FX cache + formatter helpers.
   Engineers post fees in USD; clients see local-currency equivalents.
   Rates are fetched once per page load from /api/fx/rates (backend
   caches upstream 12 h + falls back to a static table on outage). */
import { api } from "@/lib/api";

let ratesPromise = null;
let ratesCache = null;   // { base:"USD", rates:{CUR:mult}, source, fetched_at }

export async function loadRates() {
  if (ratesCache) return ratesCache;
  if (!ratesPromise) {
    ratesPromise = api.get("/fx/rates")
      .then(({ data }) => { ratesCache = data; return data; })
      .catch(() => {
        // Absolute worst case — return a USD identity so formatting still works.
        const fallback = { base: "USD", rates: { USD: 1 }, source: "client-fallback", fetched_at: null };
        ratesCache = fallback;
        return fallback;
      });
  }
  return ratesPromise;
}

/* Sensible symbols for the currencies we care about — falls back to
   the ISO code prefix when unmapped. */
const SYMBOL = {
  USD: "$", CAD: "CA$", MXN: "Mex$",
  EUR: "€", GBP: "£", CHF: "CHF", SEK: "kr", NOK: "kr", DKK: "kr",
  PLN: "zł", CZK: "Kč", HUF: "Ft", RON: "lei", ISK: "kr",
  JPY: "¥", CNY: "¥", HKD: "HK$", SGD: "S$", INR: "₹",
  KRW: "₩", TWD: "NT$", THB: "฿", MYR: "RM", IDR: "Rp",
  PHP: "₱", VND: "₫", PKR: "₨", BDT: "৳", LKR: "Rs",
  AUD: "A$", NZD: "NZ$",
  AED: "AED", SAR: "SAR", ILS: "₪", QAR: "QAR",
  ZAR: "R", NGN: "₦", EGP: "E£", KES: "KSh",
  BRL: "R$", ARS: "AR$", CLP: "CLP", COP: "COL$", PEN: "S/",
  RUB: "₽", UAH: "₴", TRY: "₺",
};

export function currencySymbol(code) {
  return SYMBOL[String(code || "USD").toUpperCase()] || `${code} `;
}

export function fmtUsd(n) {
  if (!n || Number.isNaN(Number(n))) return "$0";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/* Convert USD amount → target currency using loaded rates. Returns
   the raw number OR null if the target isn't in the rate table. */
export function convertFromUsd(amountUsd, targetCurrency) {
  if (!amountUsd || !targetCurrency) return null;
  const cur = String(targetCurrency).toUpperCase();
  if (cur === "USD") return Number(amountUsd);
  const rate = ratesCache?.rates?.[cur];
  if (!rate) return null;
  return Number(amountUsd) * rate;
}

/* Format a USD amount as "$1,000 ≈ €925" — appends the local
   equivalent only when different from USD and the rate is known. */
export function fmtDualCurrency(amountUsd, targetCurrency) {
  const usd = fmtUsd(amountUsd);
  const cur = String(targetCurrency || "USD").toUpperCase();
  if (cur === "USD" || !amountUsd) return usd;
  const local = convertFromUsd(amountUsd, cur);
  if (local == null) return usd;
  const sym = currencySymbol(cur);
  const digits = cur === "JPY" || cur === "KRW" || cur === "IDR" || cur === "VND" ? 0 : 0;
  const localStr = `${sym}${Number(local).toLocaleString(undefined, { maximumFractionDigits: digits })}`;
  return `${usd} ≈ ${localStr}`;
}
